import { Checkbox, Label } from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';

export interface ContentLockCheckboxProps {
  /** DOM id linking the checkbox to its label; must be unique on the page. */
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  /**
   * True when the lock cannot be changed here — the template owns it, or it is inherited from a
   * locked ancestor, in which case clearing it on this element would be a no-op.
   */
  disabled?: boolean;
  /** Why the lock can't be changed. Only shown when `disabled`; defaults to the editable copy. */
  disabledReason?: string;
  /** "content" for copy, "image" for an `<img>`. Drives the label wording. */
  subject?: 'content' | 'image';
}

/**
 * The "Lock this content" opt-out shared by the copy and image edit dialogs. A ticked lock writes the
 * `data-locked` marker, which the backend enforces so later prompts and QA fixes cannot rewrite the
 * user's own edit. Kept in `shared` so both dialogs — one in `entities/section-picker`, one in
 * `shared` — carry identical wording without an illegal cross-layer import.
 */
export function ContentLockCheckbox({
  id,
  checked,
  onChange,
  disabled = false,
  disabledReason,
  subject = 'content',
}: ContentLockCheckboxProps) {
  const { t } = useTranslation('common');

  // Keyed per subject rather than interpolated: the noun is grammatical, and a language that
  // inflects the surrounding sentence cannot be served by one string with a slot in it.
  const helperText = disabled
    ? (disabledReason ?? t('components.contentLockCheckbox.lockSetElsewhere'))
    : subject === 'image'
      ? t('components.contentLockCheckbox.agentWontChangeImage')
      : t('components.contentLockCheckbox.agentWontChangeContent');

  return (
    <div className="flex items-start gap-2">
      <Checkbox
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(value) => onChange(value === true)}
        className="mt-0.5"
      />
      <div className="flex flex-col gap-0.5">
        <Label htmlFor={id} className="cursor-pointer">
          {subject === 'image'
            ? t('components.contentLockCheckbox.lockThisImage')
            : t('components.contentLockCheckbox.lockThisContent')}
        </Label>
        <span className="text-muted-foreground text-xs">{helperText}</span>
      </div>
    </div>
  );
}
